import { captureException } from '@sentry/react';
import { createSlice, Slice } from '@reduxjs/toolkit';
import { isNil, filter, findIndex, get, map, reject } from 'lodash';
import Joi, { ValidationError } from 'joi';

import backToListView from 'src/actions/shared/backToListView';
import { DomainError as StandardDomainError, Unauthorized } from '@ad-scripts/application/lib/error';
import { DomainError } from 'src/models/DomainError';
import { logout } from 'src/actions/auth/authSlice';
import { registerGateway } from 'src/configuration/registerGateway';
import { registerGatewayObserverCreator } from 'src/configuration/registerGatewayObserverCreator';
import * as sharedTypes from 'src/actions/shared/sharedTypes';
import { toastError, toastSuccess } from 'src/actions/toast';
import validateType, { translateErrors } from 'src/adapters/validateType';

export const UPDATED_SUCCESSFUL = 'Update successful!';
export const FIELD_LABEL_DELIMITER = '\x01';

export interface ElementWithID {
  id: string;
}

export type CollectionState<T> = {
  values: T[];
  loadingState: 'unstarted' | 'loading' | 'succeeded' | 'failed';
  persistingState: 'unstarted' | 'persisting' | 'succeeded' | 'failed'; // Maybe needs an associated ID
  errors: Array<DomainError>;
};

export type Effects = {
  effects: {
    add: sharedTypes.AppThunk;
    fetchAll: sharedTypes.AppThunk;
    update: sharedTypes.AppThunk;
    updateBatch: sharedTypes.AppThunk;
    validate: sharedTypes.AppThunk;
    remove: sharedTypes.AppThunk;
    unload: sharedTypes.AppThunk;
    [key: string]: sharedTypes.AppThunk;
  };
};

export type Resource<T> = Slice<CollectionState<T>> & Effects;

export type UpdateOptions = {
  customToastMsg?: string;
  customErrorToastMsg?: string;
  disableWindowBack?: boolean;
  patch?: boolean;
};

export type RemoveOptions = {
  customToastMsg?: string;
  customToastErrorMsg?: string;
  timeout?: number;
};

export type AddOptions = {
  customToastMsg?: string;
  customToastErrorMsg?: string;
  disableWindowBack?: boolean;
  timeout?: number;
  withoutToast?: boolean;
  withoutToastOnError?: boolean;
};

export type OptionsType = {
  complexKey?: string[];
  getErrorMessage?: any;
  doNotSubscribe?: boolean;
  patchOnUpdate?: boolean;
  toastMessages?: {
    onAdd?: (attrs?) => void;
    onAddFail?: (attrs?, err?) => void;
    onUpdateFail?: (attrs?, err?) => void;
    onRemove?: (attrs?) => void;
    onRemoveFail?: (attrs?) => void;
    onUpdate?: (attrs?) => void;
  };
  transientAttrsFn?: <T>(data: T) => T;
  versionedUpdates?: boolean;
  transformDataBeforeStore?: (data: unknown) => unknown;
  additionalRequestParams?: (state: unknown) => Record<string, string>;
};

export default function createCollectionResource<T extends ElementWithID>({
  name,
  endpoint,
  reducers,
  effects,
  observerCreator,
  type,
  options = { complexKey: ['id'] },
}: {
  name: string;
  endpoint: any;
  reducers?: object;
  effects?: any;
  observerCreator?: any;
  type?: any;
  options?: OptionsType;
}): Resource<T> {
  const initialValues: T[] = [];
  const initialErrors: DomainError[] = [];

  const initialState: CollectionState<T> = {
    values: initialValues,
    loadingState: 'unstarted',
    persistingState: 'unstarted',
    errors: initialErrors,
  };

  const slice = createSlice({
    name: name,
    initialState,
    reducers: {
      addStarted(state) {
        state.persistingState = 'persisting';
      },
      addSuccess(state) {
        state.persistingState = 'succeeded';
      },
      // TODO: add BE error handler
      // eslint-disable-next-line
      addRequestError(state, action) {
        state.persistingState = 'failed';
      },
      addError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },

      fetchStarted(state) {
        state.loadingState = 'loading';
        state.errors = [];
      },
      fetchSuccess(state, action) {
        state.loadingState = 'succeeded';
        state.values = action.payload || [];
      },
      fetchRequestError(state) {
        state.loadingState = 'failed';
      },
      fetchError(state, action) {
        state.loadingState = 'failed';
        state.errors = action.payload;
      },

      updateStarted(state) {
        state.persistingState = 'persisting';
      },
      updateSuccess(state) {
        state.persistingState = 'succeeded';
      },
      // TODO: add BE error handler
      // eslint-disable-next-line
      updateRequestError(state, action) {
        state.persistingState = 'failed';
      },
      updateError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },
      validationError(state, action: { payload: DomainError[] }) {
        state.errors = action.payload;
      },

      deleteStarted(state) {
        state.persistingState = 'persisting';
      },
      deleteSuccess(state) {
        state.persistingState = 'succeeded';
      },
      // TODO: add BE error handler
      // eslint-disable-next-line
      deleteRequestError(state, action) {
        state.persistingState = 'failed';
      },
      deleteError(state, action) {
        state.persistingState = 'failed';
        state.errors = action.payload;
      },

      broadcast(state, action) {
        const item = action.payload;
        const index = findIndex(state.values, value =>
          (options?.complexKey || ['id']).every(key => get(value, key) === get(item, key))
        );
        // HACK: The typing situation with generics, immer, and @reduxjs/toolkit makes me want to pull
        //       my hair out. Don't copy this.
        const values = state.values as any;
        if (index >= 0) {
          values[index] = item as any;
        } else {
          values.push(item as any);
        }
      },

      broadcastDelete(state, action) {
        const itemId = action.payload;
        state.values = filter(state.values, value => value.id !== itemId) as any;
      },

      resetErrors(state, action) {
        let errs: any[] = [];
        // If an action is defined, then reset the error specified in the action - otherwise, reset all errors
        if (action.payload) {
          const attr = action.payload;
          errs = reject(state.errors, { attr });
        }

        state.persistingState = 'unstarted';
        state.errors = errs;
      },

      reset() {
        return initialState;
      },

      ...reducers,
    },
  });

  const {
    addStarted,
    addSuccess,
    addError,
    addRequestError,
    broadcast,
    broadcastDelete,
    fetchStarted,
    fetchSuccess,
    fetchError,
    fetchRequestError,
    updateStarted,
    updateSuccess,
    updateError,
    updateRequestError,
    validationError,
    deleteStarted,
    deleteSuccess,
    deleteRequestError,
    reset,
  } = slice.actions;

  const remove: sharedTypes.AppThunk =
    (params: any, opts?: RemoveOptions) =>
    async (dispatch, getState, { gateways }) => {
      try {
        const httpOptions = opts?.timeout ? { timeout: opts?.timeout } : undefined;
        dispatch(deleteStarted());
        await gateways[name].delete(params, httpOptions);
        dispatch(deleteSuccess());

        dispatch(
          toastSuccess(opts?.customToastMsg || options?.toastMessages?.onRemove?.(params) || 'Delete successful!')
        );
      } catch (err) {
        if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(deleteRequestError(err));
        }
        dispatch(
          toastError(
            opts?.customToastErrorMsg ||
              options?.toastMessages?.onRemoveFail?.(params) ||
              'There was an error while deleting this item.'
          )
        );
      }
    };

  const fetchAll: sharedTypes.AppThunk =
    (params: any, query: any, httpOptions: any) =>
    async (dispatch, getState, { gateways }) => {
      if (options.additionalRequestParams) {
        const additionalParams = options.additionalRequestParams(getState());
        params = { ...params, ...additionalParams };
      }

      try {
        dispatch(fetchStarted());
        let result = options?.doNotSubscribe
          ? await gateways[name].fetchAll(params, query, httpOptions)
          : await gateways[name].requestAll(params, query, httpOptions);

        if (options?.transformDataBeforeStore) {
          result = options.transformDataBeforeStore(result);
        }

        dispatch(fetchSuccess(result));
      } catch (err) {
        if (err instanceof StandardDomainError) {
          dispatch(fetchError(translateErrors(err.errors, options.getErrorMessage)));
        } else if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(fetchRequestError());
        }
      }
    };

  const validate: sharedTypes.AppThunk = (attrs: any) => dispatch => {
    if (!type) {
      return true;
    }
    try {
      validateType(type, attrs);
    } catch (err) {
      dispatch(validationError(translateErrors((err as any).errors, options.getErrorMessage)));
      return false;
    }
    return true;
  };

  const validateWithSchema =
    <T>(
      item: T,
      schema: Joi.Schema,
      knownErrors?: DomainError[],
      options?: { toastMessage: string; exceptionToast: string; strictValidation?: boolean }
    ) =>
    (dispatch): boolean => {
      const toastMessage = options?.toastMessage || 'Invalid entry. Please fix the errors and try again';
      const exceptionToast = options?.exceptionToast || 'Unable to save. Please verify all fields and try again';

      try {
        const combinedErrors = knownErrors ? [...knownErrors] : [];
        const { error } = schema.validate(item, {
          abortEarly: false,
          allowUnknown: !isNil(options?.strictValidation) ? options?.strictValidation : true,
          skipFunctions: true,
          errors: {
            label: false,
            wrap: {
              label: FIELD_LABEL_DELIMITER,
            },
          },
        });
        if (error) {
          const newErrors = translateJoiErrors(error);
          combinedErrors.push(...newErrors);
        }

        if (combinedErrors.length) {
          dispatch(validationError(combinedErrors));
          dispatch(toastError(toastMessage));
        }

        return !combinedErrors.length;
      } catch (err) {
        // If we are here, it is likely a bug in Joi. Log a sentry
        captureException(err, { tags: { originalItem: JSON.stringify(item, null, 2) } });
        dispatch(toastError(exceptionToast));
        return false;
      }
    };

  const update: sharedTypes.AppThunk =
    (params: any, attrs: any, opts?: UpdateOptions) =>
    async (dispatch, getState, { gateways }) => {
      try {
        dispatch(updateStarted());

        if (options.additionalRequestParams) {
          const additionalParams = options.additionalRequestParams(getState());
          params = { ...params, ...additionalParams };
        }

        let data = attrs;
        if (options?.transientAttrsFn) {
          data = options.transientAttrsFn(data);
        }

        if (options?.versionedUpdates) {
          data.version = data.version + 1;
        }

        if (options?.patchOnUpdate || opts?.patch) {
          await gateways[name].update(params, data);
        } else {
          await gateways[name].replace(params, data);
        }
        dispatch(updateSuccess());

        if (!opts?.disableWindowBack) {
          backToListView();
        }

        dispatch(toastSuccess(opts?.customToastMsg || options?.toastMessages?.onUpdate?.(attrs) || UPDATED_SUCCESSFUL));
      } catch (err) {
        if (err instanceof StandardDomainError) {
          dispatch(updateError(translateErrors(err.errors, options.getErrorMessage)));
        } else if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(updateRequestError(err));
        }
        dispatch(toastError(options?.toastMessages?.onUpdateFail?.(attrs, err) || 'There was an error while updating'));
      }
    };

  const updateBatch: sharedTypes.AppThunk =
    (attrs: any[], opts?: UpdateOptions) =>
    async (dispatch, getState, { gateways }) => {
      try {
        dispatch(updateStarted());

        await Promise.all(
          map(attrs, attr => {
            if (opts && opts.patch) {
              return gateways[name].update({ id: attr.id }, attr);
            }
            return gateways[name].replace({ id: attr.id }, attr);
          })
        );

        dispatch(updateSuccess());

        if (!(opts && opts.disableWindowBack)) {
          backToListView();
        }

        const resourceToastMsg = options?.toastMessages?.onUpdate && options.toastMessages.onUpdate(attrs);
        const toastMsg = get(opts, 'customToastMsg', resourceToastMsg);

        dispatch(toastSuccess(toastMsg ? toastMsg : UPDATED_SUCCESSFUL));
      } catch (err) {
        if (err instanceof StandardDomainError) {
          dispatch(updateError(translateErrors(err.errors, options.getErrorMessage)));
        } else if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(updateRequestError(err));
        }
        dispatch(toastError(`There was an error while updating`));
      }
    };

  const add: sharedTypes.AppThunk =
    (params: any, attrs: any, opts: AddOptions = { disableWindowBack: false }) =>
    async (dispatch, getState, { gateways }) => {
      try {
        dispatch(addStarted());

        if (options.additionalRequestParams) {
          const additionalParams = options.additionalRequestParams(getState());
          params = { ...params, ...additionalParams };
        }

        let data = attrs;
        if (options?.transientAttrsFn) {
          data = options.transientAttrsFn(data);
        }

        if (options?.versionedUpdates) {
          data.version = data.version || 1;
        }

        const httpOptions = opts?.timeout ? { timeout: opts?.timeout } : undefined;
        await gateways[name].add(params, data, httpOptions);
        dispatch(addSuccess());
        if (!opts.disableWindowBack) {
          backToListView();
        }

        // I know, we normally don't use negative form, but in other case we need to refactor a lot of code
        if (!opts?.withoutToast) {
          dispatch(toastSuccess(opts.customToastMsg || options?.toastMessages?.onAdd?.(attrs) || 'Add successful!'));
        }
      } catch (err) {
        if (err instanceof StandardDomainError) {
          dispatch(addError(translateErrors(err.errors, options.getErrorMessage)));
        } else if (err instanceof Unauthorized) {
          dispatch(logout());
        } else {
          dispatch(addRequestError(err));
        }

        if (!opts?.withoutToast && !opts?.withoutToastOnError) {
          dispatch(
            toastError(
              opts.customToastErrorMsg ||
                options?.toastMessages?.onAddFail?.(attrs, err) ||
                'There was an error while adding'
            )
          );
        }
      }
    };

  const unload: sharedTypes.AppThunk =
    (params: any) =>
    async (dispatch, getState, { gateways }) => {
      await gateways[name].unsubscribeAll(params);
      dispatch(reset());
    };

  const defaultCreateObserver = ({ dispatch }) => ({
    onBroadcast(item) {
      dispatch(broadcast(item));
    },
    onBroadcastDelete(_: any, { id }) {
      dispatch(broadcastDelete(id));
    },
  });
  const createObserver = observerCreator ? observerCreator({ actions: slice.actions }) : defaultCreateObserver;

  registerGatewayObserverCreator(name, createObserver);
  registerGateway(name, endpoint);

  const sliceWithEffects = {
    ...slice,
    effects: {
      add,
      fetchAll,
      update,
      updateBatch,
      validate,
      validateWithSchema,
      remove,
      unload,
      ...effects,
    },
  };

  return sliceWithEffects;
}

/**
 * Translates Joi error messages into the { <attr> : <message> } map that can be used with Forms
 *
 * @param error [Joi.ValidationError]
 * @param labelDelimiter [String] - single character string that delimits field label/name in the
 * error message generated by Joi
 */
export function translateJoiErrors(
  error: ValidationError,
  labelDelimiter: string = FIELD_LABEL_DELIMITER
): DomainError[] {
  if (!error?.details.length) return [];
  return error.details.map(errorDetails => {
    const regex = `${labelDelimiter}(.*?)${labelDelimiter}`;
    const path = errorDetails.path.join('.');

    const matches = errorDetails.message.match(regex);
    const fieldName = matches ? matches[1] || 'The value' : '';
    const message = errorDetails.message.replace(new RegExp(regex), '').trim();
    const errorMessage = `${fieldName.trim()} ${message}`.trim();

    return { code: 'invalid', attr: path, detail: errorMessage };
  });
}
